import WarpElement from "@warp-ds/elements-core";
import { css, html, nothing } from "lit";
import { Link, SearchFilter } from "../../models/filters.js";
import { Subvertical } from "../../constants.js";
import { i18n } from "@lingui/core";

interface ModelCountMap {
    [key: string]: number;
}

const defaultModelCount = 4;
const maxModelCount = 10;

class MakeModelSeoLinks extends WarpElement {
    declare modelCountMap: ModelCountMap;
    declare makeModelLinks: SearchFilter[];
    declare subvertical: Subvertical;
    declare showMoreMakes: boolean;

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-FgveiX;
        `,
    ];

    static properties = {
        modelCountMap: { type: Object },
        makeModelLinks: { type: Array },
        subvertical: { type: String },
        showMoreMakes: { type: Boolean },
    };

    constructor() {
        super();
        this.modelCountMap = {};
        this.showMoreMakes = false;
    }

    connectedCallback() {
        super.connectedCallback();
    }

    increaseModelsToShow(make: string) {
        const currentModelCount = this.modelCountMap?.[make];
        const newModelCount = Math.min((currentModelCount ?? defaultModelCount) + 3, maxModelCount);
        this.modelCountMap = { ...this.modelCountMap, [make]: newModelCount };
    }

    resetModelsToShow(make: string) {
        this.modelCountMap = {
            ...this.modelCountMap,
            [make]: defaultModelCount,
        };
    }

    getPageTitle(subvertical: Subvertical): string | undefined {
        switch (subvertical) {
            case Subvertical.Car:
                return i18n.t({
                    id: "make-model-links.title.car",
                    message: "Popular car brands",
                });
            case Subvertical.Boat:
                return i18n.t({
                    id: "make-model-links.title.boat",
                    message: "Popular boat brands",
                });
            case Subvertical.Mc:
                return i18n.t({
                    id: "make-model-links.title.mc",
                    message: "Popular motorcycle brands",
                });
            case Subvertical.B2B:
                return i18n.t({
                    id: "make-model-links.title.b2b",
                    message: "Popular b2b brands",
                });
        }
    }

    toggleShowMoreMakes() {
        this.showMoreMakes = !this.showMoreMakes;
    }

    get itemsToDisplay(): Array<{ title?: Link; links: Array<Link> }> {
        const makeLimit = this.showMoreMakes ? 10 : 5;

        if (this.subvertical === Subvertical.Car || this.subvertical === Subvertical.B2B) {
            return this.makeModelLinks.slice(0, makeLimit);
        }

        const makeArray = this.makeModelLinks.map((make) => make.title);

        const chunkedMakes = [];
        for (let i = 0; i < makeArray.length; i += 2) {
            chunkedMakes.push({ links: makeArray.slice(i, i + 2) });
        }

        return chunkedMakes.slice(0, makeLimit);
    }

    render() {
        return html`
            <div class="flex relative">
                <hr class="absolute left-1/2 -translate-x-1/2 w-screen" />
                <div class="max-w-[1024] flex flex-col gap-16 w-full">
                    <h3 class="t3 pt-56">${this.getPageTitle(this.subvertical)}</h3>
                    <div class="grid grid-cols-5 lt-md:grid-cols-2 lt-sm:grid-cols-1 gap-56">
                        ${this.itemsToDisplay.map(({ title, links }) => {
                            const elId = `${this.subvertical}-make-list`;
                            const modelsToShow = this.modelCountMap?.[title?.displayName] ?? defaultModelCount;
                            return html`<div>
                                ${title
                                    ? html`<label for="${elId}" class="font-bold"
                                          ><a href="${title.url}" class="s-text">${title.displayName}</a></label
                                      >`
                                    : nothing}

                                <ul id="${elId}" class="my-8 flex flex-col gap-4">
                                    ${links.slice(0, modelsToShow).map(({ displayName: model, url }, i) => {
                                        const canShowMore = maxModelCount > modelsToShow && links.length > modelsToShow;
                                        const canShowLess = maxModelCount === modelsToShow;

                                        const extraButton = () => {
                                            switch (true) {
                                                case i === modelsToShow - 1 && canShowMore:
                                                    return html`<li>
                                                        <w-button
                                                            variant="link"
                                                            @click="${() => this.increaseModelsToShow(title.displayName)}"
                                                            >${i18n.t({
                                                                id: "make-model-links.column.show-more",
                                                                message: "Show more",
                                                            })}</w-button
                                                        >
                                                    </li>`;

                                                case i === modelsToShow - 1 && canShowLess:
                                                    return html`<li>
                                                        <w-button variant="link" @click="${() => this.resetModelsToShow(title.displayName)}"
                                                            >${i18n.t({
                                                                id: "make-model-links.column.show-less",
                                                                message: "Show less",
                                                            })}</w-button
                                                        >
                                                    </li>`;

                                                default:
                                                    return nothing;
                                            }
                                        };

                                        return html`<li>
                                                <a href="${url}" class="s-text s-text-subtle">${model}</a>
                                            </li>
                                            ${extraButton()}`;
                                    })}
                                </ul>
                            </div>`;
                        })}
                    </div>
                    ${this.makeModelLinks.length > 10
                        ? html`<div>
                              <button @click="${this.toggleShowMoreMakes}" class="s-text-link bg-transparent hover:underline">
                                  ${this.showMoreMakes
                                      ? i18n.t({ id: "make-model-links.expand-makes-rows.show-less", message: "Show less makes" })
                                      : i18n.t({ id: "make-model-links.expand-makes-rows.show-more", message: "Show more makes" })}
                              </button>
                          </div>`
                        : nothing}
                </div>
            </div>
        `;
    }
}

customElements.define("mfp-make-model-seo-links", MakeModelSeoLinks);
